import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  useTheme
} from '@mui/material';
import {
  AutoFixHigh,
  Save,
  SaveAs,
  Refresh,
  Send,
  Clear,
  Edit,
  Visibility
} from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

interface Chapter {
  template_chapter_id: number;
  title: string;
  actual_title?: string;
  is_indicator_driven?: boolean;
  content_status?: string;
}

interface WritingAreaProps {
  selectedChapter: Chapter | null;
  content: string;
  onContentChange: (content: string) => void;
  onSave: (content: string, saveType: 'temp' | 'final') => Promise<boolean>;
  onGenerateDraft: (userPrompt?: string) => Promise<string | null>;
  projectId: string;
}

const WritingArea: React.FC<WritingAreaProps> = ({
  selectedChapter,
  content,
  onContentChange,
  onSave,
  onGenerateDraft
}) => {
  const theme = useTheme();
  const [localContent, setLocalContent] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generateDialog, setGenerateDialog] = useState(false);
  const [generatePrompt, setGeneratePrompt] = useState('');
  const [viewMode, setViewMode] = useState<'edit' | 'preview'>('preview');

  // 章节标题与"满意度报告"识别
  const chapterTitle = selectedChapter?.actual_title || selectedChapter?.title || '';
  const isSatisfactionReportChapter = /满意度(调查)?(分析)?报告/.test(chapterTitle);

  const getStatusProps = (status?: string) => {
    switch (status) {
      case 'final':
        return { label: '已定稿', color: 'success' as const };
      case 'draft':
        return { label: '草稿', color: 'info' as const };
      case 'user_edited':
        return { label: '待审核', color: 'warning' as const };
      case 'mentor_skipped_indicator_driven':
        return { label: '指标章节', color: 'default' as const };
      default:
        return { label: status || '未保存', color: 'default' as const };
    }
  };

  // 同步外部内容到本地状态
  useEffect(() => {
    let processedContent = content || '';
    
    // 如果内容包含转义的换行符，进行处理
    if (processedContent.includes('\\n')) {
      processedContent = processedContent.replace(/\\n/g, '\n');
    }
    
    setLocalContent(processedContent);
  }, [content]);

  // 单独处理章节切换时的视图模式重置
  useEffect(() => {
    // 当章节切换时，重置为预览模式
    setViewMode('preview');
  }, [selectedChapter?.template_chapter_id]);

  // 内容变化处理
  const handleContentChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = event.target.value;
    setLocalContent(newContent);
    onContentChange(newContent);
  };

  // 生成初稿
  const handleGenerateDraft = async () => {
    if (!selectedChapter) return;

    setGenerating(true);
    try {
      const generatedContent = await onGenerateDraft(generatePrompt);
      if (generatedContent) {
        setLocalContent(generatedContent);
        onContentChange(generatedContent);
        setGenerateDialog(false);
        setGeneratePrompt('');
        // 自动执行最终保存
        setSaving(true);
        try {
          await onSave(generatedContent, 'final');
        } finally {
          setSaving(false);
        }
      }
    } catch (error) {
    } finally {
      setGenerating(false);
    }
  };

  // 临时保存
  const handleTempSave = async () => {
    setSaving(true);
    try {
      await onSave(localContent, 'temp');
    } finally {
      setSaving(false);
    }
  };

  // 最终保存
  const handleFinalSave = async () => {
    setSaving(true);
    try {
      const success = await onSave(localContent, 'final');
      if (!success) {
      }
    } finally {
      setSaving(false);
    }
  };

  // 清空内容
  const handleClear = () => {
    setLocalContent('');
    onContentChange('');
  };

  // 刷新内容（重新加载）
  const handleRefresh = () => {
    setLocalContent(content || '');
  };

  // 处理视图模式切换
  const handleViewModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: 'edit' | 'preview' | null
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  if (!selectedChapter) {
    return (
      <Box sx={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: 'background.default'
      }}>
        <Typography variant="h6" color="text.secondary">
          请选择一个章节开始编辑
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      {/* 章节标题和操作栏 */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: 'background.default'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
            {selectedChapter.actual_title || selectedChapter.title}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip 
              label={selectedChapter.is_indicator_driven ? '指标驱动' : 'QA驱动'} 
              size="small"
              color={selectedChapter.is_indicator_driven ? 'primary' : 'secondary'}
              variant="outlined"
            />
            <Chip 
              label={`字数: ${typeof localContent === 'string' ? localContent.replace(/\s+/g, '').length : 0}`} 
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>
        
        {/* 右侧容器，包含按钮和状态 */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* 操作按钮（满意度报告不显示任何按钮） */}
          {!isSatisfactionReportChapter && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {selectedChapter.is_indicator_driven ? (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={saving ? <CircularProgress size={16} /> : <SaveAs />}
                    onClick={handleFinalSave}
                    disabled={saving}
                    color="success"
                  >
                    最终保存
                  </Button>
                  <Tooltip title="刷新内容">
                    <IconButton size="small" onClick={handleRefresh}>
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={generating ? <CircularProgress size={16} /> : <AutoFixHigh />}
                    onClick={() => setGenerateDialog(true)}
                    disabled={generating}
                  >
                    {generating ? '生成中...' : '生成初稿'}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                    onClick={handleTempSave}
                    disabled={saving}
                  >
                    临时保存
                  </Button>
                  
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={saving ? <CircularProgress size={16} /> : <SaveAs />}
                    onClick={handleFinalSave}
                    disabled={saving}
                    color="success"
                  >
                    最终保存
                  </Button>
                  
                  <Tooltip title="清空内容">
                    <span>
                      <IconButton 
                        size="small" 
                        onClick={handleClear}
                        disabled={!localContent}
                      >
                        <Clear />
                      </IconButton>
                    </span>
                  </Tooltip>
                  
                  <Tooltip title="刷新内容">
                    <IconButton size="small" onClick={handleRefresh}>
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                  
                  {/* 视图模式切换 */}
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={handleViewModeChange}
                    size="small"
                    sx={{ ml: 1 }}
                  >
                    <ToggleButton value="edit" aria-label="编辑模式">
                      <Edit fontSize="small" />
                    </ToggleButton>
                    <ToggleButton value="preview" aria-label="预览模式">
                      <Visibility fontSize="small" />
                    </ToggleButton>
                  </ToggleButtonGroup>
                </>
              )}
            </Box>
          )}

          {/* 状态标签 */}
          {selectedChapter.content_status && !isSatisfactionReportChapter && (
            <Chip
              label={getStatusProps(selectedChapter.content_status).label}
              color={getStatusProps(selectedChapter.content_status).color}
              variant="outlined"
              size="small"
            />
          )}
        </Box>

        {/* 满意度章节专用提示 */}
        {isSatisfactionReportChapter && (
          <Alert severity="info" sx={{ mt: 1 }}>
            此章节内容不支持在线写作，可以在报告导出后手动补充满意度调研结果。
          </Alert>
        )}
      </Box>

      {/* 内容编辑区 */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {(viewMode === 'edit' && !selectedChapter.is_indicator_driven && !isSatisfactionReportChapter) ? (
          <TextField
            multiline
            fullWidth
            value={localContent}
            onChange={handleContentChange}
            placeholder={
              selectedChapter.is_indicator_driven 
                ? `开始编写"${selectedChapter.actual_title || selectedChapter.title}"章节内容...

提示：
- 这是一个指标驱动的章节，点击"生成初稿"基于指标数据自动生成
- 支持Markdown格式
- 内容会自动保存为临时文件
- 完成后点击"最终保存"保存到数据库
- 点击预览按钮查看格式化效果`
                : `开始编写"${selectedChapter.actual_title || selectedChapter.title}"章节内容...

提示：
- 点击"生成初稿"让AI帮助您开始
- 支持Markdown格式  
- 内容会自动保存为临时文件
- 完成后点击"最终保存"保存到数据库
- 点击预览按钮查看格式化效果`
            }
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                height: '100%',
                alignItems: 'flex-start',
                bgcolor: 'background.paper',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                height: '100% !important',
                overflow: 'auto !important',
                resize: 'none',
                fontFamily: '"Roboto Mono", "Consolas", "Monaco", monospace',
                fontSize: '14px',
                lineHeight: 1.6,
                padding: '16px',
                paddingBottom: '48px',
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word'
              }
            }}
          />
        ) : (
          <Box sx={{
            flex: 1, 
            overflow: 'auto', 
            p: 3,
            bgcolor: 'background.paper',
            '& table': {
              width: '100%',
              borderCollapse: 'collapse',
              margin: '20px 0',
              fontSize: '14px'
            },
            '& th, & td': {
              border: `1px solid ${theme.palette.divider}`,
              padding: '8px 12px',
              textAlign: 'left'
            },
            '& th': {
              backgroundColor: theme.palette.background.paper,
              fontWeight: 'bold',
              textAlign: 'center',
              color: `${theme.palette.text.primary} !important`
            },
            '& tr:nth-of-type(even) td': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
            },
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              marginTop: '24px',
              marginBottom: '16px',
              fontWeight: '600',
              color: 'text.primary'
            },
            '& p': {
              marginBottom: '16px',
              lineHeight: 1.7
            },
            '& strong': {
              fontWeight: '600'
            },
            '& blockquote': {
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              paddingLeft: '16px',
              margin: '16px 0',
              color: theme.palette.text.secondary
            },
            '& pre': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              padding: '1em',
              borderRadius: '0.5em',
              overflow: 'auto',
              margin: '1em 0'
            },
            '& code': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
              padding: '0.125em 0.25em',
              borderRadius: '0.25em',
              fontSize: '0.875em'
            },
          }}>
            {localContent ? (
              selectedChapter.is_indicator_driven ? (
                // 指标章节直接渲染HTML内容
                <div dangerouslySetInnerHTML={{ __html: localContent }} />
              ) : (
                // 普通章节使用Markdown渲染
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                >
                  {localContent}
                </ReactMarkdown>
              )
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', mt: 4 }}>
                {isSatisfactionReportChapter
                  ? '此章节内容不支持在线写作，可以在报告导出后手动补充满意度调研结果。'
                  : '暂无内容，请切换到编辑模式开始编写或生成初稿'}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* 生成初稿对话框（满意度报告不显示） */}
      {!isSatisfactionReportChapter && (
        <Dialog 
          open={generateDialog} 
          onClose={() => setGenerateDialog(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              m: 0
            }
          }}
        >
          <DialogTitle>
            AI生成初稿 - {selectedChapter.actual_title || selectedChapter.title}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedChapter.is_indicator_driven 
                ? "此章节将基于项目指标数据生成内容。您可以添加额外要求："
                : "此章节将基于相关问答数据生成内容。您可以添加额外要求："
              }
            </Typography>
            
            <TextField
              fullWidth
              multiline
              rows={4}
              value={generatePrompt}
              onChange={(e) => setGeneratePrompt(e.target.value)}
              placeholder="例如：请重点分析存在的问题和改进建议..."
              variant="outlined"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGenerateDialog(false)}>
              取消
            </Button>
            <Button 
              onClick={handleGenerateDraft}
              variant="contained"
              disabled={generating}
              startIcon={generating ? <CircularProgress size={16} /> : <Send />}
            >
              {generating ? '生成中...' : '开始生成'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default WritingArea;
